import { SWReactIcons } from "@similarweb/icons";
import { respondTo, setFont } from "@similarweb/styles";
import { SearchInput } from "@similarweb/ui-components";
import autobind from "autobind-decorator";
import { PureComponent } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import { i18nFilter } from "app/filters/i18nFilter";
import { allTrackers } from "../../../../services/track/track";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { clearSelectedTemplate, selectDashboardTemplate, templateSetParent } from "../actions/dashboardTemplateActions";
import { DashboardTemplateService, EDashboardParentType } from "../DashboardTemplateService";
import type { IDashboardTemplate } from "../DashboardTemplateService";
import { DashboardTemplate } from "./DashboardTemplate";
import { addTemplateKeys } from "app/pages/digital-suite/home-page/components/CustomAssets/customAssetsUtils";
import { swSettings } from "app/common/services/swSettings";
import DashboardService from "app/components/dashboard/services/DashboardService";

const widths = {
    large: 1137,
    medium: 1063,
    small: 805,
};

const upgrade = (title) => {
    allTrackers.trackEvent("Contactus/Contact Us Page", "click", `dashboard gallery/${title}`);
    window.open("https://www.similarweb.com/corp/contact/");
};

const DashboardTemplateGrid = styled.div`
    width: ${widths.large}px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 63px;
    padding-bottom: 50px;
    @media (max-width: 1440px) {
        width: ${widths.medium}px;
        column-gap: 26px;
    }
    @media (max-width: 1366px) {
        width: 1027px;
        column-gap: 6px;
    }
    ${respondTo(
        "desktop",
        css`
            width: 100%;
            gap: 39px;
            column-gap: 39px;
            padding: 0 109px;
            box-sizing: border-box;
            grid-template-columns: 1fr 1fr;
        `,
    )};
`;

const DashboardTemplateGalleryHeader = styled.div`
    width: ${widths.large}px;
    margin: 0 auto !important;
    flex-direction: row-reverse;
    @media (max-width: 1440px) {
        width: ${widths.medium}px;
    }
    ${respondTo(
        "mediumScreen",
        css`
            width: 1027px;
            padding: 0;
            box-sizing: border-box;
        `,
    )};
    ${respondTo(
        "desktop",
        css`
            width: 932px;
            padding: 0 109px;
            box-sizing: border-box;
        `,
    )};
`;

const DashboardTemplateNoResults = styled.div`
    height: 316px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 72px;
`;

const DashboardTemplateNoResultsIcon = styled(SWReactIcons)`
    height: 48px;
    width: 48px;
    margin-bottom: 23px;
`;

const DashboardTemplateNoResultsIconTitle = styled.div`
    ${setFont({ $color: "rgba(42,62,82,0.6)", $size: 24 })};
    margin-bottom: 10px;
`;

const DashboardTemplateNoResultsIconSubTitle = styled(DashboardTemplateNoResultsIconTitle)`
    ${setFont({ $size: 14 })};
`;

interface IDashboardTemplateGalleryProps {
    onTemplateClick: (templateName: string) => void;
    clearSelectedTemplate: () => void;
    selectedTemplate: number | string;
}

interface IDashboardTemplateGalleryState {
    loading: boolean;
    // templates?: dashboardTemplate[];
    templates?: IDashboardTemplate[];
    search?: string;
}

class DashboardTemplateGallery extends PureComponent<IDashboardTemplateGalleryProps, IDashboardTemplateGalleryState> {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
        };
    }

    async componentDidMount() {
        if (this.props.selectedTemplate === "blank") {
            this.props.clearSelectedTemplate();
        }
        const templates = await DashboardTemplateService.generateDashboardTemplates();
        this.setState({
            loading: false,
            templates,
        });
    }

    public render() {
        if (this.state.loading) {
            return this.renderLoader();
        } else {
            return this.renderGallery();
        }
    }

    private renderLoader() {
        return null;
    }

    private renderGallery() {
        const { onTemplateClick } = this.props;
        const search = (this.state.search || "").toLowerCase();
        const templates = this.state.templates.filter((template) => {
            const { title, description, sticky } = template;
            // return sticky || _.includes(title.toLowerCase(), search) || _.includes(description.toLowerCase(), search);
            return (
                i18nFilter()(title).toLowerCase().includes(search) ||
                i18nFilter()(description).toLowerCase().includes(search)
            );
        });
        return (
            <div>
                <DashboardTemplateGalleryHeader className="react-wizard-filter">
                    <SearchInput
                        isFocused={true}
                        placeholder={i18nFilter()("dashboard.metricGallery.searchPlaceholder")}
                        className="react-wizard-filter-input"
                        onChange={this.handleSearch}
                        onBlur={this.track}
                    />
                </DashboardTemplateGalleryHeader>
                {templates.length > 0 ? (
                    <DashboardTemplateGrid>
                        {templates.map((template, index) => {
                            const {
                                id,
                                locked,
                                thumbnailImage,
                                title,
                                description,
                                empty,
                                isNew,
                                autoFillUserDomain,
                                autoFillIndustries,
                            } = template;
                            return (
                                <DashboardTemplate
                                    id={id}
                                    key={`template${index}`}
                                    locked={locked}
                                    empty={empty}
                                    image={thumbnailImage}
                                    title={i18nFilter()(title)}
                                    description={i18nFilter()(description)}
                                    onClick={
                                        !locked
                                            ? onTemplateClick.bind(
                                                  null,
                                                  id,
                                                  title,
                                                  !!autoFillUserDomain,
                                                  !!autoFillIndustries,
                                              )
                                            : upgrade.bind(this, title)
                                    }
                                    isNew={isNew}
                                />
                            );
                        })}
                    </DashboardTemplateGrid>
                ) : (
                    <DashboardTemplateNoResults>
                        <DashboardTemplateNoResultsIcon iconName="no-search-results" />
                        <DashboardTemplateNoResultsIconTitle>
                            <I18n>dashboard.template.nosearchresults.title</I18n>
                        </DashboardTemplateNoResultsIconTitle>
                        <DashboardTemplateNoResultsIconSubTitle>
                            <I18n>dashboard.template.nosearchresults.tryagain</I18n>
                        </DashboardTemplateNoResultsIconSubTitle>
                    </DashboardTemplateNoResults>
                )}
            </div>
        );
    }

    @autobind
    private handleSearch(search) {
        this.setState({
            search,
        });
    }

    private track = () => {
        // for tracking only after the user start's to input in the search bar
        if (this.state.search !== undefined) {
            if (this.state.search.length > 0) {
                allTrackers.dispatchTrackEvent(
                    "Search Bar",
                    "click",
                    `Search Bar/click/${(this.state.search ? this.state.search : null) || ""}`,
                );
            } else {
                allTrackers.dispatchTrackEvent("Search Bar", "remove", "Search Bar/remove");
            }
        }
    };
}

function mapStateToProps(state) {
    return {
        selectedTemplate: state.customDashboard.dashboardTemplate.selectedTemplate,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onTemplateClick: (id, title: string, autoFillUserDomain: boolean) => {
            allTrackers.trackEvent("dashboard template", "click", title);
            dispatch(selectDashboardTemplate(id));
            dispatch(templateSetParent(EDashboardParentType.TEMPLATE, id));

            //automatically fill out the user's domain and similar sites
            if (autoFillUserDomain) {
                const activationWizardCache = JSON.parse(window.localStorage.getItem("ActivationWizardItems")) ?? {};
                const fromWizard = activationWizardCache.isCreateDashboardFromHP;
                const firstDashboard = !DashboardService.dashboards.length;
                const userDomain = swSettings.user?.customData?.quiz?.website;
                const userDomainValid = !!userDomain && userDomain !== "no_website";

                if (userDomainValid && (fromWizard || firstDashboard)) {
                    addTemplateKeys(dispatch, userDomain);
                }
            }
        },
        clearSelectedTemplate: () => dispatch(clearSelectedTemplate()),
    };
}

const connected = connect(mapStateToProps, mapDispatchToProps)(DashboardTemplateGallery);
export default connected;
